import useSWR from 'swr';
import api from 'utils/api';
import type { Artist, Pagination, PaginatedResponse, UserInfo } from 'types';

// ===== Admin Collab Club requests listing types (GET /admin/cc-requests) =====
// Co-located here because they describe this endpoint's request/response contract
// and are only consumed by this hook and the Collab Club admin page.
// (The generic Pagination / PaginatedResponse / UserInfo live in `types`.)

// Target ("wants to collab with") cell. `ccMatchId` is the FK to cc_matches —
// non-null means this pair is already matched (connected).
export interface AdminCcRequestArtist {
  id: number;
  ccArtistId: number;
  ccArtist: Artist;
  ccMatchId: number | null;
}

export interface AdminCcRequest {
  id: number;
  userId: number;
  source: 'dashboard' | 'manual';
  requestDate: string | null;
  createdAt: string;
  updatedAt: string;
  fromCcArtist: Artist | null;
  requestArtists: AdminCcRequestArtist[];
  arUser: UserInfo | null;
}

// One expected match from POST /admin/cc-requests/preview (read-only match check).
// `artist1` = the requester, `artist2` = the artist that already points back.
// `artist1User`/`artist2User` are the artists' claimed accounts (the intro recipients);
// `arUser1`/`arUser2` are their A&Rs (CC'd on the intro).
export interface CcMatchPreview {
  artist1: Artist;
  artist2: Artist;
  artist1User: UserInfo | null;
  artist2User: UserInfo | null;
  arUser1: UserInfo | null;
  arUser2: UserInfo | null;
  method: 'auto' | 'team';
}

export type CcRequestOrderBy =
  | 'name'
  | 'genres'
  | 'location'
  | 'monthlyListeners'
  | 'arUser'
  | 'requestDate'
  | 'source';

export interface AdminCcRequestsParams {
  scope?: 'mine' | 'all';
  search?: string;
  orderBy?: CcRequestOrderBy;
  orderDirection?: 'ASC' | 'DESC';
  page?: number;
  limit?: number;
}

interface UseAdminCcRequestsResult {
  requests: AdminCcRequest[];
  pagination: Pagination | null;
  totalItems: number;
  isLoading: boolean;
  error: any;
  refetch: () => void;
}

/**
 * Lists Collab Club requests for the admin page (GET /admin/cc-requests).
 * Server-side scope / search / sort / pagination — see backend docs/collab-club-api.md.
 */
export function useAdminCcRequests(params: AdminCcRequestsParams): UseAdminCcRequestsResult {
  // Drop empty values so the request only carries meaningful query params.
  const queryParams: Record<string, string | number> = {};
  if (params.scope) queryParams.scope = params.scope;
  if (params.search?.trim()) queryParams.search = params.search.trim();
  if (params.orderBy) queryParams.orderBy = params.orderBy;
  if (params.orderDirection) queryParams.orderDirection = params.orderDirection;
  if (params.page) queryParams.page = params.page;
  if (params.limit) queryParams.limit = params.limit;

  const {
    data,
    error,
    mutate: refetch,
  } = useSWR<PaginatedResponse<AdminCcRequest>>(
    ['admin-cc-requests', JSON.stringify(queryParams)],
    () => api.collabClub.getAdminRequests({ params: queryParams }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  return {
    requests: data?.data || [],
    pagination: data?.pagination || null,
    totalItems: data?.pagination?.totalItems ?? 0,
    isLoading: !data && !error,
    error,
    refetch,
  };
}
